import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, concatMap, map, merge, of, take } from 'rxjs';

import { SelectAccountingSystemsModalComponent } from '../modals/select-accounting-systems-modal/select-accounting-systems-modal.component';
import { ApConfigService } from './ap-config.service';

// VPDW-763: shared orchestration for "designate the accounting system(s) a vendor
// is created in." Consumed by the create flows (client-registered + exempt) so the
// pop-up rules live in one place.
@Injectable({
  providedIn: 'root'
})
export class AccountingSystemSelectionService {
  constructor(
    private ModalService: NgbModal,
    private ApConfigService: ApConfigService
  ) {}

  /**
   * Resolves which accounting system(s) a vendor should be created in:
   * - 0 eligible systems -> `undefined` (caller omits apConfigIds; unchanged behavior).
   * - exactly 1          -> `[thatId]` (no pop-up; auto-selected).
   * - 2+                 -> opens the selector, resolving to the chosen ids, or
   *                         `null` if the user cancels (caller should abort the save).
   * Pass `CompanyId` for the Admin surface; the Client surface is auto-scoped.
   */
  selectApConfigIds(
    CompanyId?: number
  ): Observable<number[] | null | undefined> {
    return this.ApConfigService.getCreateEligibleApConfigs(CompanyId).pipe(
      take(1),
      concatMap((apConfigs) => {
        if (!apConfigs || apConfigs.length === 0) {
          return of(undefined);
        }

        if (apConfigs.length === 1) {
          return of([apConfigs[0].id as number]);
        }

        const modalRef = this.ModalService.open(
          SelectAccountingSystemsModalComponent,
          { centered: true }
        );
        modalRef.componentInstance.ApConfigs = apConfigs;

        return merge(
          (modalRef.closed as Observable<number[]>).pipe(
            map((apConfigIds: number[]) => apConfigIds)
          ),
          (modalRef.dismissed as Observable<unknown>).pipe(map(() => null))
        );
      })
    );
  }
}
